/**
 * Fetches the configured ticker list and keeps per-ticker status.
 *
 * Returns three views over the same data:
 *   • `tickers`  — flat list (preserves backend order).
 *   • `statuses` — map keyed by ticker symbol with the full status row.
 *   • `groups`   — { equities } for UI components that render the pill
 *                  section (TickerPills on the Options heatmap page).
 *                  Everything is an equity now.
 */

import { useEffect, useMemo, useState } from 'react';
import { apiGet } from '../lib/api';
import { TickerStatus } from '../lib/types';

interface TickersPayload {
  tickers: TickerStatus[];
}

export interface TickerGroups {
  equities: string[];
}

interface UseTickersReturn {
  tickers: string[];
  statuses: Record<string, TickerStatus>;
  groups: TickerGroups;
  loading: boolean;
  error: string | null;
}

export function useTickers(): UseTickersReturn {
  const [tickers, setTickers] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<Record<string, TickerStatus>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    apiGet<TickersPayload>('/tickers', ctrl.signal)
      .then(data => {
        const list = data.tickers.map(t => t.ticker);
        const map: Record<string, TickerStatus> = {};
        data.tickers.forEach(t => { map[t.ticker] = t; });
        setTickers(list);
        setStatuses(map);
        setError(null);
      })
      .catch(err => {
        if (err?.name === 'AbortError') return;
        setError(err?.message ?? 'Failed to load tickers');
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, []);

  const groups = useMemo<TickerGroups>(() => {
    // Everything is an equity — just mirror the flat list in order.
    return { equities: [...tickers] };
  }, [tickers]);

  return { tickers, statuses, groups, loading, error };
}
